import { h } from '../charts/svg.ts'
import { renderBars, renderCumulative, renderLegend, seriesColor, type ChartSeries } from '../charts/chart.ts'
import { aggregate, type SeriesSource } from '../report/aggregate.ts'
import { renderDataTable } from './datatable.ts'
import { fetchFilterValues, type Attribute, type FilterValue } from '../sdk/attributes.ts'
import { createSearchCache, type Table } from '../sdk/search.ts'
import type { Bucket, Window } from '../lib/time.ts'
import { DL_TO_L } from '../report/flow.ts'
import { periodFor } from '../report/fetchFlow.ts'
import { displayUnit, type Dimension } from '../lib/units.ts'

export type ChartKind = 'cumulative' | 'bars' | 'both'

export interface CustomSeries {
  id: string
  attribute: Attribute
  filterId: string | null
  filterValue: string | null
  filterLabel: string | null
  /** Raw → base: dL attributes become litres, dL/min become L/min. */
  scale: number
  /** What the base quantity is; picks the display unit (and unit system). */
  dimension: Dimension
  /** Suffix for a raw quantity the app cannot convert, e.g. "%" or "mV". */
  unitSuffix: string
  slot: number
}

let seq = 0

/**
 * Machine-name suffixes give away the unit: `_dl` is decilitres, `_dl_min`
 * decilitres per minute. Both convert to a base the unit system can render;
 * anything else is shown raw with whatever unit its display name states.
 */
function scaleFor(attr: Attribute): { scale: number; dimension: Dimension; suffix: string } {
  if (/_dl_min$/i.test(attr.name)) return { scale: DL_TO_L, dimension: 'flowrate', suffix: '' }
  if (/_dl$/i.test(attr.name)) return { scale: DL_TO_L, dimension: 'volume', suffix: '' }
  const unit = /\(([^)]+)\)\s*$/.exec(attr.desc)?.[1]
  return { scale: 1, dimension: 'raw', suffix: unit ?? '' }
}

export interface BuilderState {
  series: CustomSeries[]
  kind: ChartKind
  title: string
  /** Fetched tables survive re-renders (unit toggle, dev toggle); the key
   *  carries window, bucket and filter, so a real change still refetches. */
  cache: ReturnType<typeof createSearchCache>
}

export function createBuilderState(): BuilderState {
  return { series: [], kind: 'both', title: 'Custom graph', cache: createSearchCache() }
}

export interface BuilderDeps {
  attributes: Attribute[]
  interfaceId: number
  window: Window
  bucket: Bucket
  serverPeriod: boolean
  devMode: boolean
  onChange: () => void
}

export function renderBuilder(state: BuilderState, deps: BuilderDeps): HTMLElement {
  const host = h('div', { class: 'card builder' })
  const output = h('div', { style: 'display:grid;gap:16px' })

  // --- add-series controls -------------------------------------------------
  const attrSel = h('select', { id: 'b-attr' }) as HTMLSelectElement
  attrSel.append(h('option', { value: '', text: 'Choose an attribute…' }))
  const groups = new Map<string, Attribute[]>()
  for (const a of deps.attributes) {
    if (!groups.has(a.groupDesc)) groups.set(a.groupDesc, [])
    groups.get(a.groupDesc)!.push(a)
  }
  for (const [group, list] of groups) {
    const og = h('optgroup', { label: group })
    for (const a of list) {
      og.append(
        h('option', {
          value: a.treeId,
          text: a.available ? `${a.desc} · ${a.name}` : `${a.desc} — not collected`,
          disabled: !a.available,
        }),
      )
    }
    attrSel.append(og)
  }

  const filterSel = h('select', { id: 'b-filter', disabled: true }) as HTMLSelectElement
  const valueSel = h('select', { id: 'b-value', disabled: true }) as HTMLSelectElement
  const addBtn = h('button', { class: 'btn btn-primary', type: 'button', text: 'Add series', disabled: true })

  const selectedAttr = () => deps.attributes.find((a) => a.treeId === attrSel.value) ?? null

  function resetSelect(sel: HTMLSelectElement, placeholder: string, enabled: boolean) {
    sel.replaceChildren(h('option', { value: '', text: placeholder }))
    sel.disabled = !enabled
  }

  attrSel.addEventListener('change', async () => {
    const attr = selectedAttr()
    addBtn.disabled = !attr
    resetSelect(filterSel, 'No filter (all)', false)
    resetSelect(valueSel, 'All values', false)
    if (!attr || !attr.filters.length) return
    for (const f of attr.filters) filterSel.append(h('option', { value: f.filter_id, text: f.filter_name }))
    filterSel.disabled = false
  })

  filterSel.addEventListener('change', async () => {
    const attr = selectedAttr()
    resetSelect(valueSel, 'All values', false)
    if (!attr || !filterSel.value) return
    valueSel.append(h('option', { value: '', text: 'Loading…' }))
    valueSel.disabled = true
    try {
      const values: FilterValue[] = await fetchFilterValues(deps.interfaceId, attr.searchId, filterSel.value)
      resetSelect(valueSel, 'All values', true)
      for (const v of values) valueSel.append(h('option', { value: v.value, text: v.label }))
    } catch {
      // A filter-value list the cloud won't serve shouldn't block the series:
      // fall back to "all values", which is a valid search.
      resetSelect(valueSel, 'All values (list unavailable)', false)
    }
  })

  addBtn.addEventListener('click', () => {
    const attr = selectedAttr()
    if (!attr) return
    const { scale, dimension, suffix } = scaleFor(attr)
    const used = new Set(state.series.map((s) => s.slot))
    let slot = 1
    while (used.has(slot) && slot < 8) slot++
    state.series.push({
      id: `s${++seq}`,
      attribute: attr,
      filterId: filterSel.value || null,
      filterValue: valueSel.value || null,
      filterLabel: valueSel.value ? valueSel.options[valueSel.selectedIndex]?.text ?? null : null,
      scale,
      dimension,
      unitSuffix: suffix,
      slot,
    })
    attrSel.value = ''
    addBtn.disabled = true
    resetSelect(filterSel, 'No filter (all)', false)
    resetSelect(valueSel, 'All values', false)
    deps.onChange()
  })

  const kindSel = h('select', { id: 'b-kind' }) as HTMLSelectElement
  for (const [value, label] of [
    ['both', 'Cumulative + per-bucket'],
    ['cumulative', 'Cumulative only'],
    ['bars', 'Per-bucket bars only'],
  ] as const) {
    const o = h('option', { value, text: label })
    if (state.kind === value) o.selected = true
    kindSel.append(o)
  }
  kindSel.addEventListener('change', () => {
    state.kind = kindSel.value as ChartKind
    deps.onChange()
  })

  host.append(
    h(
      'div',
      { class: 'section-head' },
      h('h2', { text: 'Custom graph' }),
      h('p', { text: 'Any attribute the cloud offers, over the same period and bucket as the report above.' }),
    ),
    h(
      'div',
      { class: 'builder-row' },
      h('div', { class: 'field' }, h('label', { htmlFor: 'b-attr', text: 'Attribute' }), attrSel),
      h('div', { class: 'field' }, h('label', { htmlFor: 'b-filter', text: 'Filter' }), filterSel),
      h('div', { class: 'field' }, h('label', { htmlFor: 'b-value', text: 'Filter value' }), valueSel),
      h('div', { class: 'field' }, h('label', { htmlFor: 'b-kind', text: 'Chart' }), kindSel),
      addBtn,
    ),
  )

  // --- current series ------------------------------------------------------
  if (state.series.length) {
    host.append(
      h(
        'div',
        { class: 'series-list' },
        ...state.series.map((s) => {
          const remove = h('button', { class: 'btn btn-ghost btn-sm', type: 'button', text: 'Remove' })
          remove.addEventListener('click', () => {
            state.series = state.series.filter((x) => x.id !== s.id)
            deps.onChange()
          })
          return h(
            'div',
            { class: 'series-row' },
            h('span', { class: 'series-swatch', style: `background:${seriesColor(s.slot)}` }),
            h(
              'div',
              {},
              h('div', { text: s.attribute.desc }),
              h('div', {
                class: 'series-meta',
                text:
                  `id ${s.attribute.searchId} · ${s.attribute.name}` +
                  (s.filterLabel ? ` · ${s.filterLabel}` : s.filterId ? ' · all values' : '') +
                  (s.dimension === 'volume' ? ' · volume' : s.dimension === 'flowrate' ? ' · flow rate' : s.unitSuffix ? ` · ${s.unitSuffix}` : ''),
              }),
            ),
            remove,
          )
        }),
      ),
    )
  } else {
    host.append(h('div', { class: 'empty', text: 'Add an attribute to plot it.' }))
  }

  host.append(output)
  if (state.series.length) void run(state, deps, output)
  return host
}

async function run(state: BuilderState, deps: BuilderDeps, output: HTMLElement) {
  const progress = h(
    'div',
    { class: 'progress' },
    h('div', { class: 'spinner' }),
    h('span', { text: 'Fetching…' }),
  )
  output.replaceChildren(progress)

  const cache = state.cache
  const sources: SeriesSource[] = []
  const tables: { series: CustomSeries; table: Table; period: string | null }[] = []
  try {
    for (const s of state.series) {
      const period = periodFor(s.attribute, deps.bucket, deps.serverPeriod)
      const table = await cache.fetch(deps.interfaceId, deps.window, {
        attribute: s.attribute.searchId,
        filter: s.filterId,
        filter_value: s.filterValue,
        period,
      })
      tables.push({ series: s, table, period })
      sources.push({
        key: s.id,
        label: s.filterLabel ? `${s.attribute.desc} · ${s.filterLabel}` : s.attribute.desc,
        column: s.attribute.name,
        table,
        sourceKey: s.id,
        scale: s.scale,
      })
    }
  } catch (err) {
    output.replaceChildren(
      h(
        'div',
        { class: 'banner banner-critical' },
        h('span', { class: 'banner-icon', text: '!' }),
        h('span', { text: err instanceof Error ? err.message : String(err) }),
      ),
    )
    return
  }

  const agg = aggregate(sources, deps.window, deps.bucket)
  // Mixed units on one value axis would be a lie; say so rather than draw it.
  const kinds = new Set(state.series.map((s) => (s.dimension === 'raw' ? `raw:${s.unitSuffix}` : s.dimension)))
  const first = state.series[0]!
  const maxBase = Math.max(1e-9, ...agg.series.flatMap((s) => [...s.buckets, ...s.cumulative]).filter(Number.isFinite))
  const unit = kinds.size === 1 ? displayUnit(first.dimension, maxBase, first.unitSuffix) : { suffix: '', scale: 1, decimals: 1 }
  const suffix = unit.suffix
  const unitList = state.series
    .map((s) => `${s.attribute.desc}: ${s.dimension === 'raw' ? s.unitSuffix || 'no unit' : s.dimension}`)
    .join(' · ')

  const bars: ChartSeries[] = agg.series.map((s, i) => ({
    key: s.key,
    label: s.label,
    slot: state.series[i]?.slot ?? 8,
    values: s.buckets,
  }))
  const cum: ChartSeries[] = agg.series.map((s, i) => ({
    key: s.key,
    label: s.label,
    slot: state.series[i]?.slot ?? 8,
    values: s.cumulative,
  }))

  const blocks: (HTMLElement | null)[] = []
  if (kinds.size > 1) {
    blocks.push(
      h(
        'div',
        { class: 'banner banner-warning' },
        h('span', { class: 'banner-icon', text: '!' }),
        h('span', {
          text: `These series carry different units (${unitList}). One value axis cannot state both — plot them separately to compare honestly.`,
        }),
      ),
    )
  }
  if (state.kind !== 'bars') {
    blocks.push(
      h(
        'div',
        { class: 'chart-block' },
        h('div', { class: 'chart-title', text: `Accumulated over the period${suffix ? ` (${suffix})` : ''}` }),
        renderCumulative({ bucketStarts: agg.bucketStarts, bucket: agg.bucket, series: cum, unit, height: 260 }),
      ),
    )
  }
  if (state.kind !== 'cumulative') {
    blocks.push(
      h(
        'div',
        { class: 'chart-block' },
        h('div', { class: 'chart-title', text: `Per ${agg.bucket}${suffix ? ` (${suffix})` : ''}` }),
        renderBars({ bucketStarts: agg.bucketStarts, bucket: agg.bucket, series: bars, unit, height: 200 }),
        renderLegend(bars),
      ),
    )
  }
  if (deps.devMode) {
    for (const t of tables) {
      blocks.push(
        renderDataTable(
          t.series.attribute.desc,
          `attribute ${t.series.attribute.searchId} (${t.series.attribute.name}) · period ${t.period ?? 'none'}` +
            (t.series.filterLabel ? ` · ${t.series.filterLabel}` : ''),
          t.table,
        ),
      )
    }
  }
  output.replaceChildren(...blocks.filter((b): b is HTMLElement => b !== null))
}
